import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Description,
  Download,
  CheckCircle,
  Warning,
  Send,
} from '@mui/icons-material';
import {
  providerAssessmentAPI,
  PatientAssessment,
  AssessmentSummary,
  FMLAFormRequest,
  FMLAFormResponse,
  assessmentUtils,
} from '../../services/providerAssessmentService';

interface FMLAFormGeneratorProps {
  appointmentId: string;
  assessments: PatientAssessment[];
  summary: AssessmentSummary | null;
  eligible: boolean;
}

const FMLAFormGenerator: React.FC<FMLAFormGeneratorProps> = ({
  appointmentId,
  assessments,
  summary,
  eligible,
}) => {
  const [formData, setFormData] = useState<FMLAFormRequest>({
    appointmentId,
    diagnosis: '',
    clinicalFindings: '',
    functionalLimitations: '',
    workRestrictions: '',
    startDate: new Date().toISOString().split('T')[0], // Today's date
    estimatedEndDate: '',
  });
  const [generating, setGenerating] = useState(false);
  const [generatedForm, setGeneratedForm] = useState<FMLAFormResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleInputChange = (field: keyof FMLAFormRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const generatePreFilledData = () => {
    const phq9 = assessmentUtils.getAssessmentByType(assessments, 'phq9');
    const gad7 = assessmentUtils.getAssessmentByType(assessments, 'gad7');
    const biopsycho = assessmentUtils.getAssessmentByType(assessments, 'biopsychosocial');

    let diagnosis = '';
    let clinicalFindings = '';
    let functionalLimitations = '';
    let workRestrictions = '';

    // Generate diagnosis based on scores
    if (phq9?.totalScore && phq9.totalScore >= 10) {
      diagnosis += `Major Depressive Disorder (PHQ-9 score: ${phq9.totalScore}, ${phq9.scoreLevel}). `;
    }
    if (gad7?.totalScore && gad7.totalScore >= 10) {
      diagnosis += `Generalized Anxiety Disorder (GAD-7 score: ${gad7.totalScore}, ${gad7.scoreLevel}). `;
    }

    // Clinical findings
    clinicalFindings = `Patient completed standardized assessments revealing: `;
    if (phq9?.scoreInterpretation) {
      clinicalFindings += `Depression: ${phq9.scoreInterpretation}. `;
    }
    if (gad7?.scoreInterpretation) {
      clinicalFindings += `Anxiety: ${gad7.scoreInterpretation}. `;
    }
    if (biopsycho) {
      clinicalFindings += `Comprehensive biopsychosocial assessment completed documenting significant functional impairment.`;
    }

    // Functional limitations
    if (phq9?.totalScore && phq9.totalScore >= 10) {
      functionalLimitations += 'Difficulty concentrating, decreased energy, sleep disturbances, ';
    }
    if (gad7?.totalScore && gad7.totalScore >= 10) {
      functionalLimitations += 'Excessive worry, restlessness, difficulty controlling anxiety, ';
    }
    functionalLimitations += 'impaired ability to maintain consistent work performance and attendance.';

    // Work restrictions
    workRestrictions = eligible 
      ? 'Temporary leave recommended with gradual return to work as clinically appropriate. May require flexible schedule or reduced hours during initial return period.'
      : 'Further evaluation recommended to determine appropriate work accommodations.';

    setFormData(prev => ({
      ...prev,
      diagnosis: diagnosis.trim(),
      clinicalFindings: clinicalFindings.trim(),
      functionalLimitations: functionalLimitations.trim(),
      workRestrictions: workRestrictions.trim(),
      estimatedEndDate: getEstimatedEndDate(phq9?.totalScore ?? 0, gad7?.totalScore ?? 0),
    }));
  };

  const getEstimatedEndDate = (phq9Score: number, gad7Score: number): string => {
    // Estimate duration based on severity
    let weeksEstimate = 4; // Default 4 weeks
    
    if (phq9Score >= 15 || gad7Score >= 15) {
      weeksEstimate = 12; // Severe: 12 weeks
    } else if (phq9Score >= 10 || gad7Score >= 10) {
      weeksEstimate = 8; // Moderate: 8 weeks
    }

    const endDate = new Date();
    endDate.setDate(endDate.getDate() + (weeksEstimate * 7));
    return endDate.toISOString().split('T')[0];
  };

  const generateForm = async () => {
    try {
      setGenerating(true);
      setError(null);

      const response = await providerAssessmentAPI.generateFMLAForm(appointmentId, formData);
      setGeneratedForm(response.data);
      setPreviewDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate FMLA form');
    } finally {
      setGenerating(false);
    }
  };

  const sendFormByEmail = async () => {
    if (!generatedForm) return;
    
    try {
      setSendingEmail(true);
      setError(null);

      const patientEmail = assessments.length > 0 ? assessments[0].patientEmail : '';
      
      await providerAssessmentAPI.sendFMLAFormByEmail(
        appointmentId, 
        generatedForm.formId,
        {
          patientEmail,
          includePatient: true,
          includeEmployer: false, // Could add UI option for this
        }
      );

      setEmailSent(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send FMLA form by email');
    } finally {
      setSendingEmail(false);
    }
  };

  const isFormValid = () => {
    return formData.diagnosis?.trim() && 
           formData.clinicalFindings?.trim() && 
           formData.startDate && 
           formData.estimatedEndDate;
  };

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: eligible ? 'success.50' : 'warning.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Description sx={{ mr: 1, color: eligible ? 'success.main' : 'warning.main' }} />
          <Typography variant="h6">
            FMLA Certification Form
          </Typography>
          <Chip 
            label={eligible ? 'Patient Eligible' : 'Review Required'}
            color={eligible ? 'success' : 'warning'}
            sx={{ ml: 2 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {eligible 
            ? 'Patient meets clinical criteria for FMLA certification. Complete the form below to generate the official certification.'
            : 'Patient may not meet standard criteria. Clinical judgment and additional evaluation may be required.'
          }
        </Typography>
      </Paper>

      {/* Pre-fill Assistant */}
      {!formData.diagnosis && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={generatePreFilledData}>
              Auto-Fill from Assessments
            </Button>
          }
        >
          Use assessment results to automatically populate form fields based on clinical data.
        </Alert>
      )}

      {/* Form Fields */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Clinical Diagnosis *"
            multiline
            rows={3}
            fullWidth
            value={formData.diagnosis}
            onChange={handleInputChange('diagnosis')}
            placeholder="Enter primary diagnosis based on assessment results..."
            helperText="Include relevant ICD-10 or DSM-5 codes if applicable"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Clinical Findings *"
            multiline
            rows={4}
            fullWidth
            value={formData.clinicalFindings}
            onChange={handleInputChange('clinicalFindings')}
            placeholder="Describe clinical findings that support the diagnosis..."
            helperText="Include assessment scores, symptoms, and clinical observations"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Functional Limitations"
            multiline
            rows={3}
            fullWidth
            value={formData.functionalLimitations}
            onChange={handleInputChange('functionalLimitations')}
            placeholder="Describe how the condition affects patient's ability to work..."
            helperText="Be specific about work-related functional impairments"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Work Restrictions"
            multiline
            rows={3}
            fullWidth
            value={formData.workRestrictions}
            onChange={handleInputChange('workRestrictions')}
            placeholder="Specify work restrictions and accommodations..."
            helperText="Include recommendations for leave duration and return-to-work plans"
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            label="Start Date *"
            type="date"
            fullWidth
            value={formData.startDate}
            onChange={handleInputChange('startDate')}
            InputLabelProps={{ shrink: true }}
            helperText="Date when leave should begin"
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            label="Estimated End Date"
            type="date"
            fullWidth
            value={formData.estimatedEndDate}
            onChange={handleInputChange('estimatedEndDate')}
            InputLabelProps={{ shrink: true }}
            helperText="Expected return to work date"
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Generate Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setPreviewDialogOpen(true)}
          disabled={!isFormValid()}
        >
          Preview Form
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={generateForm}
          disabled={!isFormValid() || generating}
          startIcon={generating ? <CircularProgress size={20} /> : <Description />}
          sx={{ minWidth: 200 }}
        >
          {generating ? 'Generating...' : 'Generate FMLA Form'}
        </Button>
      </Box>

      {/* Form Preview/Download Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle color="success" sx={{ mr: 1 }} />
            FMLA Certification Form
          </Box>
        </DialogTitle>
        <DialogContent>
          {generatedForm ? (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Form Generated Successfully
                </Typography>
                <Typography variant="body2">
                  Validation Code: <strong>{generatedForm.validationCode}</strong>
                </Typography>
                <Typography variant="body2">
                  Generated: {new Date(generatedForm.generatedAt).toLocaleString()}
                </Typography>
              </Alert>
              
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Form Summary
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Diagnosis:</strong> {formData.diagnosis}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Duration:</strong> {formData.startDate} to {formData.estimatedEndDate}
                  </Typography>
                  <Typography variant="body2">
                    The official FMLA certification has been generated and is ready for download.
                    This form meets all Department of Labor requirements for FMLA certification.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Form Preview
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Diagnosis:</strong> {formData.diagnosis || 'Not specified'}
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Clinical Findings:</strong> {formData.clinicalFindings || 'Not specified'}
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Duration:</strong> {formData.startDate || 'Not specified'} to {formData.estimatedEndDate || 'Not specified'}
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Click "Generate FMLA Form" to create the official certification document.
                </Alert>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            Close
          </Button>
          {generatedForm && (
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => window.open(generatedForm.pdfUrl, '_blank')}
            >
              Download PDF
            </Button>
          )}
          {generatedForm && !emailSent && (
            <Button
              variant="outlined"
              startIcon={sendingEmail ? <CircularProgress size={16} /> : <Send />}
              onClick={sendFormByEmail}
              disabled={sendingEmail}
            >
              {sendingEmail ? 'Sending...' : 'Send to Patient'}
            </Button>
          )}
          {emailSent && (
            <Chip 
              label="Email Sent Successfully" 
              color="success"
              icon={<CheckCircle />}
            />
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FMLAFormGenerator;